import moment from 'moment';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Toggle from 'react-toggle';
import MonthYearPicker from '@/shared/components/input/MonthYearPicker';
import CompanyMultiSelect from '@/shared/components/input/multiselect/CompanyMultiSelect';
import TeamMultiSelect from '@/shared/components/input/multiselect/TeamMultiSelect';
import HasMultiCompany from '@/shared/components/security/HasMultiCompany';
import AuthService from '@/shared/services/AuthService';
import UserSearch from '@/shared/components/input/UserSearch';
import EnumMultiSelect from '@/shared/components/input/multiselect/EnumMultiSelect';
import ResourceAutoComplete from '@/shared/components/registration/ResourceAutoComplete';
import ResourceMultiSelect from '@/shared/components/input/multiselect/ResourceMultiSelect';
import { FaUndo } from 'react-icons/fa';
import EmployeeTagMultiSelect from '@/modules/hrm/EmployeeTagMultiSelect';
import messages from '../reportingMessages';
import AbsenceCalendarPeriods from './AbsenceCalendarPeriods';

export const defaultFilters = {
  userId: null,
  ownCompanies: [],
  employeeTypes: [],
  resources: [],
  teams: [],
  year: moment.utc().year(),
  month: moment.utc().month(),

  onlyWithAbsences: true,
  withEmployeeLabels: [],
};

const AbsenceCalendarFilters = ({ filters, onFilterChange, canSeeAbsenceDetails }) => {
  const intl = useIntl();
  const filterChanged = filter => {
    const newFilters = { ...filters, ...filter };

    if (JSON.stringify(filters) !== JSON.stringify(newFilters)) onFilterChange(newFilters);
  };
  const userChanged = item => {
    filterChanged({
      userId: (item && item.id) || null,
    });
  };

  const resetFilters = () => {
    filterChanged({ ...defaultFilters });
  };

  const { year, month } = filters;
  const showUsers = AuthService.hasPermission({
    functionality: 'RegistrationOthers',
    privilege: 'Read',
  });

  const hasFilters = JSON.stringify(filters) !== JSON.stringify(defaultFilters);

  return (
    <div>
      <div className="row">
        <div className="col-lg-3 col-md-6">
          <div className="form-group">
            <MonthYearPicker value={{ year, month }} onChange={filterChanged} />
          </div>
        </div>
        {showUsers && (
          <>
            <div className="col-lg-3 col-md-6">
              <div className="form-group">
                <UserSearch
                  onChange={userChanged}
                  value={filters.userId}
                  placeholder={intl.formatMessage(messages.AllUsers)}
                />
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="form-group">
                <EnumMultiSelect
                  type="employeeType"
                  selection={filters.employeeTypes}
                  orderByLabel
                  onChange={value => filterChanged({ employeeTypes: value })}
                  placeholder={intl.formatMessage(messages.EmployeeType)}
                />
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="form-group">
                <TeamMultiSelect
                  selection={filters.teams}
                  onChange={value => filterChanged({ teams: value })}
                />
              </div>
            </div>
          </>
        )}
        <div className="col-lg-6 col-md-12">
          <AbsenceCalendarPeriods filters={filters} onChangeDate={filterChanged} />
        </div>
        {!!canSeeAbsenceDetails && (
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <ResourceMultiSelect
                resourceTypes={[3, 4]}
                selection={filters.resources}
                onChange={resources => filterChanged({ resources })}
                placeholder={intl.formatMessage(messages.AbsenceTypes)}
              />
            </div>
          </div>
        )}
        <div className="col-lg-3 col-md-6">
          <div className="form-group">
            <EmployeeTagMultiSelect
              value={filters.withEmployeeLabels}
              onChange={withEmployeeLabels => filterChanged({ withEmployeeLabels })}
              placeholder={intl.formatMessage(messages.WithEmployeeTags)}
            />
          </div>
        </div>
        {showUsers && (
          <div className="col-lg-3 col-md-6">
            <label className="react-toggle-wrapper margin5T">
              <Toggle
                checked={filters.onlyWithAbsences}
                onChange={e => filterChanged({ onlyWithAbsences: e.target.checked })}
              />
              <FormattedMessage className="label-text" {...messages.OnlyEmployeesWithAbsences} />
            </label>
          </div>
        )}
        <HasMultiCompany>
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <CompanyMultiSelect
                selection={filters.ownCompanies || []}
                onChange={companies => filterChanged({ ownCompanies: companies })}
              />
            </div>
          </div>
        </HasMultiCompany>
      </div>
      <div className="flex-row">
        <div className="btn-group save-filters">
          {hasFilters && (
            <button type="button" className="btn btn-default btn-reset" onClick={resetFilters}>
              <FaUndo className="react-icon" />
              &nbsp;
              <FormattedMessage {...messages.ResetFilters} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AbsenceCalendarFilters;
